/**
 * Turns an engine prescription into the strings the UI shows.
 *
 * Kept separate from `engine.js` so the engine stays pure and unit-agnostic:
 * it reasons entirely in kg (the unit `sets.weight_kg` stores), and this module
 * is the only place that knows the user might read pounds.
 */

import { legacy } from '../../runtime/legacy.js';

/** kg → the user's display unit, via the legacy helpers so rounding matches the rest of the app. */
export function displayWeight(weightKg) {
  const toDisplay = legacy('kgToDisplay');
  const label = legacy('getUnitLabel');
  const value = typeof toDisplay === 'function' ? toDisplay(weightKg) : Math.round(weightKg * 10) / 10;
  const unit = typeof label === 'function' ? label() : 'kg';
  return `${value}${unit}`;
}

function uniform(values) {
  return values.every((v) => v === values[0]);
}

/**
 * "85kg × 8" for a uniform prescription, "85kg × 12/11/10/9" when only the rep
 * targets differ, and the full per-set list when the loads differ too.
 *
 * That last case is not hypothetical: a prescription built from a ramped
 * session carries a different load per set, and printing `prescription[0]`'s
 * weight across all of them — as this did — showed the lifter their opening
 * warm-up as if it were the whole session.
 */
export function summarise(result) {
  if (!result?.prescription?.length) return null;
  const sets = result.prescription;
  const weights = sets.map((s) => s.weightKg);
  const reps = sets.map((s) => s.reps);

  if (!uniform(weights)) {
    return sets.map((s) => `${displayWeight(s.weightKg)} × ${s.reps}`).join(' · ');
  }
  const weight = displayWeight(weights[0]);
  return uniform(reps) ? `${weight} × ${reps[0]}` : `${weight} × ${reps.join('/')}`;
}

/**
 * "4 × 85kg × 8" — the form that reads best on a compact card. A ramp has no
 * single load to put in front of a set count, so it names the top set instead.
 */
export function summariseWithSetCount(result) {
  if (!result?.prescription?.length) return null;
  const sets = result.prescription;
  const count = sets.length;

  if (!uniform(sets.map((s) => s.weightKg))) {
    const top = sets.reduce((best, s) => (s.weightKg > best.weightKg ? s : best), sets[0]);
    return `${count} sets · top ${displayWeight(top.weightKg)} × ${top.reps}`;
  }
  return `${count} × ${summarise(result)}`;
}

/**
 * The single sentence explaining *why*. This is the part the plan calls the
 * product: a number with no reason is indistinguishable from a guess.
 *
 * The engine reasons entirely in kg and leaves a `{load}` token wherever a load
 * delta belongs, rather than formatting one itself. Substituting it here is what
 * stops an imperial user reading "176.4lbs" next to "adding 2.27kg".
 */
export function explain(result) {
  const reason = substituteLoad(result);
  // The set-count recommendation is a second sentence rather than a second
  // field, so every surface that already renders the reason picks it up.
  return result?.setNote ? `${reason} ${result.setNote}` : reason;
}

function substituteLoad(result) {
  const reason = result?.reason || '';
  if (!reason.includes('{load}')) return reason;
  const delta = result?.loadDeltaKg;
  if (typeof delta !== 'number' || delta <= 0) {
    // No delta to name — drop the clause rather than print a placeholder.
    return reason.replace(/\s*—\s*adding \{load\}/, '').replace('{load}', 'weight');
  }
  return reason.replace('{load}', displayWeight(delta));
}

/** True when there is nothing to prescribe and the UI should say so instead of guessing. */
export function isColdStart(result) {
  return !result?.hasHistory;
}
